import {
    Box,
    Card,
    CardContent,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    makeStyles,
    Typography,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import { useEntity } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';

const LIFECYCLE_PHASES = ['poc', 'dev', 'production', 'end-of-life'] as const;
type Phase = (typeof LIFECYCLE_PHASES)[number];

const PHASE_CONFIG: Record<Phase, { label: string; color: string }> = {
    poc: { label: 'PoC', color: '#FF9800' },
    dev: { label: 'Dev', color: '#2196F3' },
    production: { label: 'Prod', color: '#4CAF50' },
    'end-of-life': { label: 'End-of-Life', color: '#9E9E9E' },
};

interface CriterionCheck {
    label: string;
    annotationKey?: string;
    requiredValue?: string;
}

const PHASE_CRITERIA: Record<Phase, CriterionCheck[]> = {
    poc: [
        { label: 'Ziel/Scope dokumentiert', annotationKey: 'itkom.pbs.ch/scope-documented', requiredValue: 'true' },
        { label: 'Minimaler Funktionsnachweis', annotationKey: 'itkom.pbs.ch/poc-validated', requiredValue: 'true' },
        { label: 'DSFA (falls relevant)', annotationKey: 'itkom.pbs.ch/dsfa-status', requiredValue: 'completed' },
        { label: 'Architektur-Zeichnung', annotationKey: 'itkom.pbs.ch/architecture-diagram', requiredValue: 'completed' },
        { label: 'Portmatrix', annotationKey: 'itkom.pbs.ch/portmatrix', requiredValue: 'completed' },
        { label: 'Go/No-Go Entscheid', annotationKey: 'itkom.pbs.ch/go-nogo', requiredValue: 'approved' },
    ],
    dev: [
        { label: 'Health Endpoint vorhanden', annotationKey: 'itkom.pbs.ch/health-endpoint', requiredValue: 'true' },
        { label: 'Logs/Metriken via App Insights', annotationKey: 'itkom.pbs.ch/monitoring-status', requiredValue: 'basic' },
        { label: 'Azure Budget aktiviert', annotationKey: 'itkom.pbs.ch/cost-budget', requiredValue: 'active' },
        { label: 'Monitoring/Alerting nachgewiesen', annotationKey: 'itkom.pbs.ch/alerting-status', requiredValue: 'active' },
        { label: 'Dependencies aktuell', annotationKey: 'itkom.pbs.ch/dependencies-current', requiredValue: 'true' },
        { label: 'DSFA abgeschlossen', annotationKey: 'itkom.pbs.ch/dsfa-status', requiredValue: 'completed' },
        { label: 'Benefit/Use Case validiert', annotationKey: 'itkom.pbs.ch/usecase-validated', requiredValue: 'true' },
        { label: 'Go/No-Go Entscheid', annotationKey: 'itkom.pbs.ch/go-nogo', requiredValue: 'approved' },
    ],
    production: [
        { label: 'Betriebsverantwortung definiert und Übergabe an APM', annotationKey: 'itkom.pbs.ch/ops-owner', requiredValue: 'assigned' },
        { label: 'Monitoring/Alerting aktiv', annotationKey: 'itkom.pbs.ch/monitoring-status', requiredValue: 'active' },
        { label: 'Dependency-Updates regelmässig', annotationKey: 'itkom.pbs.ch/dependencies-current', requiredValue: 'true' },
        { label: 'Kostenkontrolle aktiv', annotationKey: 'itkom.pbs.ch/cost-budget', requiredValue: 'active' },
        { label: 'Usage-/PowerBI-Report', annotationKey: 'itkom.pbs.ch/usage-report', requiredValue: 'active' },
        { label: 'Quartals-Review durchgeführt', annotationKey: 'itkom.pbs.ch/quarterly-review', requiredValue: 'true' },
    ],
    'end-of-life': [
        { label: 'Daten archiviert/gelöscht', annotationKey: 'itkom.pbs.ch/data-archived', requiredValue: 'true' },
        { label: 'Azure Ressourcen entfernt', annotationKey: 'itkom.pbs.ch/azure-cleanup', requiredValue: 'true' },
        { label: 'Secrets/Zugriffe gelöscht', annotationKey: 'itkom.pbs.ch/secrets-removed', requiredValue: 'true' },
        { label: 'Git Repo archiviert', annotationKey: 'itkom.pbs.ch/repo-archived', requiredValue: 'true' },
    ],
};

const useStyles = makeStyles(theme => ({
    card: {
        height: '100%',
    },
    phaseRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    phaseBadge: {
        fontWeight: 700,
        color: '#fff',
    },
    progressBar: {
        display: 'flex',
        width: '100%',
        height: 8,
        borderRadius: 4,
        overflow: 'hidden',
        marginTop: theme.spacing(1),
        marginBottom: theme.spacing(1),
    },
    progressSegment: {
        height: '100%',
    },
    criterionIcon: {
        minWidth: 32,
    },
    completedIcon: {
        color: '#4CAF50',
        fontSize: '1rem',
    },
    pendingIcon: {
        color: '#FF9800',
        fontSize: '1rem',
    },
    missingIcon: {
        color: theme.palette.text.disabled,
        fontSize: '1rem',
    },
    criterionText: {
        '& .MuiListItemText-primary': {
            fontSize: '0.8rem',
        },
    },
    listItem: {
        paddingTop: 2,
        paddingBottom: 2,
    },
}));

function getLifecycle(entity: Entity): Phase {
    const annotationPhase = getAnnotation(entity, 'itkom.pbs.ch/lifecycle-phase');
    const raw = annotationPhase ?? (entity.spec as { lifecycle?: string })?.lifecycle ?? 'unknown';
    const normalized = raw.toLowerCase().replace(/[_\s]/g, '-');
    if (LIFECYCLE_PHASES.includes(normalized as Phase)) {
        return normalized as Phase;
    }
    if (normalized === 'experimental') return 'poc';
    if (normalized === 'development') return 'dev';
    return 'poc';
}

function getAnnotation(entity: Entity, key: string): string | undefined {
    return entity.metadata.annotations?.[key];
}

function evaluateCriterion(
    entity: Entity,
    criterion: CriterionCheck,
): 'met' | 'pending' | 'missing' {
    if (!criterion.annotationKey) return 'missing';
    const value = getAnnotation(entity, criterion.annotationKey);
    if (!value) return 'missing';
    if (value === criterion.requiredValue) return 'met';
    return 'pending';
}

export const LifecycleStatusCard = () => {
    const classes = useStyles();
    const { entity } = useEntity();
    const phase = getLifecycle(entity);
    const phaseIndex = LIFECYCLE_PHASES.indexOf(phase);
    const criteria = PHASE_CRITERIA[phase];

    const evaluated = criteria.map(c => ({
        ...c,
        status: evaluateCriterion(entity, c),
    }));

    const metCount = evaluated.filter(e => e.status === 'met').length;
    const total = evaluated.length;
    const progressPct = total > 0 ? Math.round((metCount / total) * 100) : 0;

    const previousUnmet = LIFECYCLE_PHASES.slice(0, phaseIndex).flatMap(prevPhase =>
        PHASE_CRITERIA[prevPhase]
            .map(c => ({ ...c, phase: prevPhase, status: evaluateCriterion(entity, c) }))
            .filter(c => c.status !== 'met'),
    );

    return (
        <Card className={classes.card} variant="outlined">
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Lifecycle Status
                </Typography>

                {/* Current phase */}
                <Box className={classes.phaseRow}>
                    <Typography variant="body2">Aktuelle Phase:</Typography>
                    <Chip
                        label={PHASE_CONFIG[phase].label}
                        size="small"
                        className={classes.phaseBadge}
                        style={{ backgroundColor: PHASE_CONFIG[phase].color }}
                    />
                </Box>

                {/* Phase progress indicator */}
                <Box className={classes.progressBar}>
                    {LIFECYCLE_PHASES.map((p, i) => (
                        <Box
                            key={p}
                            className={classes.progressSegment}
                            style={{
                                flex: 1,
                                backgroundColor:
                                    i <= phaseIndex ? PHASE_CONFIG[p].color : '#e0e0e0',
                                marginRight: i < 3 ? 2 : 0,
                                borderRadius:
                                    i === 0
                                        ? '4px 0 0 4px'
                                        : i === 3
                                            ? '0 4px 4px 0'
                                            : undefined,
                            }}
                        />
                    ))}
                </Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                    {LIFECYCLE_PHASES.map(p => (
                        <Typography
                            key={p}
                            variant="caption"
                            style={{
                                fontWeight: p === phase ? 700 : 400,
                                color: p === phase ? PHASE_CONFIG[p].color : '#999',
                                fontSize: '0.6rem',
                            }}
                        >
                            {PHASE_CONFIG[p].label}
                        </Typography>
                    ))}
                </Box>

                <Divider />

                {/* Criteria checklist */}
                <Box mt={1}>
                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {phase === 'production'
                            ? 'Wartungs-Checkliste'
                            : phase === 'end-of-life'
                                ? 'Cleanup-Checkliste'
                                : 'Exit-Kriterien'}{' '}
                        ({metCount}/{total} · {progressPct}%)
                    </Typography>
                    <List dense disablePadding>
                        {evaluated.map(c => (
                            <ListItem key={c.label} className={classes.listItem}>
                                <ListItemIcon className={classes.criterionIcon}>
                                    {c.status === 'met' ? (
                                        <CheckCircleIcon className={classes.completedIcon} />
                                    ) : c.status === 'pending' ? (
                                        <HourglassEmptyIcon className={classes.pendingIcon} />
                                    ) : (
                                        <RadioButtonUncheckedIcon
                                            className={classes.missingIcon}
                                        />
                                    )}
                                </ListItemIcon>
                                <ListItemText
                                    primary={c.label}
                                    className={classes.criterionText}
                                />
                            </ListItem>
                        ))}
                    </List>
                </Box>
                {previousUnmet.length > 0 && (
                    <>
                        <Divider style={{ marginTop: 8 }} />
                        <Box mt={1}>
                            <Typography variant="body2" style={{ fontWeight: 600, color: '#f44336' }}>
                                Offene Aufgaben aus Vorphasen ({previousUnmet.length})
                            </Typography>
                            <List dense disablePadding>
                                {previousUnmet.map((c, idx) => (
                                    <ListItem key={idx} className={classes.listItem}>
                                        <ListItemIcon className={classes.criterionIcon}>
                                            {c.status === 'pending' ? (
                                                <HourglassEmptyIcon className={classes.pendingIcon} />
                                            ) : (
                                                <RadioButtonUncheckedIcon className={classes.missingIcon} />
                                            )}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={c.label}
                                            secondary={`Phase: ${PHASE_CONFIG[c.phase].label}`}
                                            className={classes.criterionText}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </>
                )}
            </CardContent>
        </Card>
    );
};
